import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgentState, CallState, ConferenceState, UnavailableCode } from '../../types';
import { logger } from '../../utils/logger';
import { loadAcwPendingContext } from './acw-pending-context';
import { resolveIsInAcwState } from './acw-detection.util';
import {
  getAcwLockedContactId,
  isAcwUiLocked,
  lockAcwUi,
  shouldIgnoreStaleLiveCall,
  unlockAcwUi,
} from './acw-ui-lock';
import { initialConferenceState } from './types';

interface UseAcwStateOptions {
  callState: CallState;
  isRinging: boolean;
  agentState: AgentState;
  unavailableCodes: UnavailableCode[];
  isInAcw: boolean;
}

function isLiveCall(callState: CallState, isRinging: boolean): boolean {
  return (
    isRinging ||
    callState.status === 'ringing' ||
    callState.status === 'connecting' ||
    callState.status === 'active' ||
    callState.status === 'holding'
  );
}

function isStaleLiveCallResurrection(callState: CallState, isRinging: boolean): boolean {
  if (!isLiveCall(callState, isRinging)) {
    return false;
  }
  return shouldIgnoreStaleLiveCall(callState.contactId);
}

export function useAcwState({
  callState,
  isRinging,
  agentState,
  unavailableCodes,
  isInAcw,
}: UseAcwStateOptions) {
  const [showACW, setShowACW] = useState(false);
  const [acwFromCallEnd, setAcwFromCallEnd] = useState(false);
  const [conferenceState, setConferenceState] = useState<ConferenceState>(initialConferenceState);

  const prevCallStatusRef = useRef<string>(callState.status);
  const callEndTriggeredRef = useRef<boolean>(false);
  const acwCompletedRef = useRef<boolean>(false);

  const staleLiveCall = isStaleLiveCallResurrection(callState, isRinging);
  const callIsLive = isLiveCall(callState, isRinging) && !staleLiveCall;

  const beginAcw = useCallback((contactId?: string | null, source = 'unknown') => {
    const resolvedContactId =
      contactId ?? getAcwLockedContactId() ?? loadAcwPendingContext()?.contactId ?? null;

    acwCompletedRef.current = false;
    setAcwFromCallEnd(true);
    setShowACW(true);
    lockAcwUi(resolvedContactId);
    logger.lifecycle('[ACW Flow] beginAcw', {
      source,
      contactId: resolvedContactId,
      acwLocked: isAcwUiLocked(),
    });
  }, []);

  useEffect(() => {
    if (callIsLive) {
      // Stale post-call SDK events must not tear down an active ACW session.
      if (isAcwUiLocked()) {
        logger.lifecycle('[ACW Flow] ignoring live-call signal while ACW locked', {
          callStatus: callState.status,
          contactId: callState.contactId,
          lockedContactId: getAcwLockedContactId(),
        });
        return;
      }

      if (showACW) {
        logger.lifecycle('[ACW Flow] hiding ACW — call became live', {
          callStatus: callState.status,
          contactId: callState.contactId,
        });
      }

      callEndTriggeredRef.current = false;
      if (showACW) {
        setShowACW(false);
        setAcwFromCallEnd(false);
      }
      unlockAcwUi();
      return;
    }

    const isInACW = resolveIsInAcwState(isInAcw, agentState, unavailableCodes);

    if (!isInACW) {
      acwCompletedRef.current = false;
    }

    if (isInACW && !showACW && !acwCompletedRef.current) {
      beginAcw(
        callState.contactId ?? getAcwLockedContactId() ?? loadAcwPendingContext()?.contactId,
        'sdk-detection'
      );
    } else if (!isInACW && showACW) {
      const agentAvailable = agentState.state?.toLowerCase() === 'available';
      if (!acwFromCallEnd || agentAvailable) {
        logger.lifecycle('[ACW Flow] hiding ACW — agent left ACW state', {
          agentState: agentState.state,
          agentReason: agentState.reason,
          acwFromCallEnd,
        });
        setShowACW(false);
        setAcwFromCallEnd(false);
        unlockAcwUi();
      }
    }
  }, [
    agentState,
    unavailableCodes,
    isInAcw,
    showACW,
    acwFromCallEnd,
    callIsLive,
    beginAcw,
    callState.contactId,
    callState.status,
  ]);

  // Resume ACW after call drop when context was persisted (same path as page refresh).
  useEffect(() => {
    if (callIsLive || showACW || acwCompletedRef.current) {
      return;
    }

    const pending = loadAcwPendingContext();
    if (!pending) {
      return;
    }

    const isInACW = resolveIsInAcwState(isInAcw, agentState, unavailableCodes);
    if (!isInACW) {
      logger.lifecycle('[ACW Flow] pending context skipped — agent not in ACW', {
        agentState: agentState.state,
        agentReason: agentState.reason,
        sdkIsInAcw: isInAcw,
        pendingContactId: pending.contactId,
      });
      return;
    }

    beginAcw(pending.contactId, 'pending-context');
  }, [callIsLive, showACW, isInAcw, agentState, unavailableCodes, beginAcw]);

  useEffect(() => {
    const prevStatus = prevCallStatusRef.current;
    const currentStatus = callState.status;

    if (currentStatus === 'active' && prevStatus !== 'active') {
      callEndTriggeredRef.current = false;
    }

    if (
      (prevStatus === 'active' ||
        prevStatus === 'ringing' ||
        prevStatus === 'holding' ||
        prevStatus === 'connecting') &&
      currentStatus === 'disconnected' &&
      !isRinging
    ) {
      const resolvedContactId =
        callState.contactId ?? getAcwLockedContactId() ?? loadAcwPendingContext()?.contactId;
      const inAcw = resolveIsInAcwState(isInAcw, agentState, unavailableCodes);

      if (!resolvedContactId && !inAcw) {
        logger.lifecycle('[ACW Flow] call-end transition skipped — no contact and not in ACW', {
          prevStatus,
          currentStatus,
        });
      } else if (!callEndTriggeredRef.current) {
        callEndTriggeredRef.current = true;
        setConferenceState(initialConferenceState);
        logger.lifecycle('[ACW Flow] call status transition ended', {
          prevStatus,
          currentStatus,
          contactId: resolvedContactId,
        });
        beginAcw(resolvedContactId, 'call-end-transition');
      } else {
        logger.lifecycle('[ACW Flow] call-end transition skipped — already triggered', {
          prevStatus,
          currentStatus,
          contactId: resolvedContactId,
        });
      }
    }

    prevCallStatusRef.current = currentStatus;
  }, [
    callState.status,
    callState.contactId,
    isRinging,
    beginAcw,
    isInAcw,
    agentState,
    unavailableCodes,
  ]);

  const handleCallEnd = useCallback(() => {
    setConferenceState(initialConferenceState);
  }, []);

  const handleTransferComplete = useCallback(() => {}, []);

  const handleConferenceInitiate = useCallback((target?: string) => {
    setConferenceState({
      status: 'consulting',
      consultContactId: null,
      consultTarget: target ?? null,
    });
  }, []);

  const dismissAcw = useCallback(() => {
    logger.lifecycle('[ACW Flow] dismissAcw');
    acwCompletedRef.current = true;
    setShowACW(false);
    setAcwFromCallEnd(false);
    unlockAcwUi();
  }, []);

  const visibleShowACW = useMemo(
    () => (showACW && !callIsLive) || (isAcwUiLocked() && showACW),
    [showACW, callIsLive]
  );

  useEffect(() => {
    logger.lifecycle('[ACW Flow] state snapshot', {
      showACW,
      visibleShowACW,
      acwFromCallEnd,
      callIsLive,
      callStatus: callState.status,
      contactId: callState.contactId,
      sdkIsInAcw: isInAcw,
      agentState: agentState.state,
      agentReason: agentState.reason,
      acwLocked: isAcwUiLocked(),
      lockedContactId: getAcwLockedContactId(),
      callEndTriggered: callEndTriggeredRef.current,
      acwCompleted: acwCompletedRef.current,
    });
  }, [
    showACW,
    visibleShowACW,
    acwFromCallEnd,
    callIsLive,
    callState.status,
    callState.contactId,
    isInAcw,
    agentState.state,
    agentState.reason,
  ]);

  return {
    showACW: visibleShowACW,
    conferenceState,
    handleCallEnd,
    handleTransferComplete,
    handleConferenceInitiate,
    dismissAcw,
  };
}
